import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  makeStyles,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { useApi, identityApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface Announcement {
  id: string;
  title: string;
  excerpt: string;
  publisher: string;
  created_at: string;
  category?: { slug: string; title: string };
  active: boolean;
}

const useStyles = makeStyles(theme => ({
  banner: {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    color: '#fff',
    position: 'relative' as const,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid rgba(255,255,255,0.15)',
    '&:last-child': {
      borderBottom: 'none',
      paddingBottom: 0,
    },
  },
  itemTitle: {
    color: '#fff',
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  itemExcerpt: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: '0.85rem',
    marginTop: 2,
  },
  itemDate: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap' as const,
    marginTop: 2,
  },
  chip: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: '#fff',
    height: 20,
    fontSize: '0.7rem',
  },
  viewAll: {
    color: 'rgba(255,255,255,0.8)',
  },
}));

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return 'just now';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

export const AnnouncementsBanner = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { token } = await identityApi.getCredentials();
        const resp = await fetchApi.fetch(
          '/api/announcements/announcements?max=3',
          token ? { headers: { Authorization: `Bearer ${token}` } } : {},
        );
        if (!resp.ok) return;
        const data = await resp.json();
        const items = data.results ?? data ?? [];
        if (!cancelled && Array.isArray(items)) {
          setAnnouncements(items.filter((a: Announcement) => a.active !== false).slice(0, 3));
        }
      } catch {
        // announcements API not available yet — hide banner silently
      }
    }
    load();
    return () => { cancelled = true; };
  }, [identityApi, fetchApi]);

  if (announcements.length === 0) return null;

  return (
    <Card className={classes.banner} variant="outlined">
      <CardContent>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <AnnouncementIcon />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Announcements
            </Typography>
            <Chip
              size="small"
              label={`${announcements.length} new`}
              className={classes.chip}
            />
          </Box>
          <Tooltip title="View all announcements">
            <IconButton
              size="small"
              component={RouterLink}
              to="/announcements"
              className={classes.viewAll}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {announcements.map(a => (
          <Box key={a.id} className={classes.item}>
            <Box flex={1}>
              <Typography
                variant="subtitle1"
                component={RouterLink}
                to={`/announcements/view/${a.id}`}
                className={classes.itemTitle}
              >
                {a.title}
              </Typography>
              {a.excerpt && (
                <Typography className={classes.itemExcerpt}>
                  {a.excerpt.length > 120 ? `${a.excerpt.slice(0, 120)}...` : a.excerpt}
                </Typography>
              )}
            </Box>
            <Box textAlign="right">
              <Typography className={classes.itemDate}>
                {formatDate(a.created_at)}
              </Typography>
              {a.category && (
                <Chip size="small" label={a.category.title} className={classes.chip} />
              )}
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};
